import { useMemo, useState } from 'react';
import { ArrowRight, FileText, Package, ScanLine, Search } from 'lucide-react';
import {
  type NomenclatureDto,
} from '@eombor/contracts';
import { Badge, EmptyState, Input, Skeleton, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import type { ReadyHandoverRow } from '../model/handover.queries.js';

/** Miqdorni ekranga chiqarish — `numeric(14,3)` bilan bir xil yumaloqlash. */
const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

/** Kartochkada ko'rinadigan satr soni — qolgani sanoq bo'lib qisqaradi. */
const SHOWN_LINES = 3;

export interface ScanStepProps {
  /** Berishga tayyor talabnomalar — ekranning asosiy mazmuni. */
  ready: readonly ReadyHandoverRow[];
  /** Nomenklatura nomi va birligi — `id` bo'yicha xarita. */
  nomenclatures: ReadonlyMap<string, NomenclatureDto>;
  onPick: (requisitionId: string) => void;
  loading: boolean;
  /** Tayyor matn (i18n dan) — bosqich uni o'zi tarjima qilmaydi. */
  error: string | null;
}

/**
 * BERISHGA TAYYOR TALABNOMALAR — oqimning birinchi ekrani.
 *
 * Ekranda ikki narsa bor: qidiruv va kartochkalar. Skaner maydoni YO'Q —
 * ombordagi pistolet klaviatura bo'lib yozadi va uni `HandoverPage`
 * oyna darajasida tinglaydi, ya'ni skanerlangan kod talabnomani maydonsiz
 * ham ochadi. Maydon esa ekranning yarmini egallab turardi va unga
 * amalda hech kim yozmasdi.
 */
export const ScanStep = ({
  ready,
  nomenclatures,
  onPick,
  loading,
  error,
}: ScanStepProps) => {
  const [query, setQuery] = useState('');

  /*
   * Qidiruv TO'RT maydon bo'yicha: qog'ozdagi «ТРЕБОВАНИЕ №», ichki
   * hujjat raqami, maqsad va mahsulot nomi. Skladchiga qaysi biri
   * ma'lum bo'lsa — o'shani teradi; qaysi maydonni tanlash kerakligini
   * o'ylab o'tirmaydi.
   */
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return ready;
    return ready.filter((row) => {
      const names = row.lines
        .map((line) => nomenclatures.get(line.nomenclatureId)?.name ?? '')
        .join(' ');
      return [row.formNo ?? '', row.requisition.docNo, row.requisition.purpose, names]
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
  }, [ready, query, nomenclatures]);

  return (
    <div className="flex flex-col gap-5">
      {/*
        SKANER MAYDONI YO'Q — va bu ataylab.
        Ombordagi pistolet klaviatura bo'lib yozadi, ya'ni u ekranda
        maydon bo'lishini TALAB QILMAYDI: `HandoverPage` oynani doimiy
        tinglaydi va skanerlangan kod talabnomani o'zi ochadi. Maydon esa
        ekranning yarmini egallab, har kirganda «bu yerga nima yozay?»
        degan savol tug'dirardi — aslida u yerga hech kim yozmasdi.

        Qolgani ikkitasi: QIDIRUV (skaner ishlamagan kun yoki qog'ozsiz
        kelgan odam uchun) va KARTOCHKALAR — ishning o'zi.
      */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-tight text-text">
          {t('handover.readyTitle')}
        </h2>

        <Input
          variant="filter"
          aria-label={t('handover.searchLabel')}
          placeholder={t('handover.searchPlaceholder')}
          value={query}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => setQuery(event.target.value)}
          prefix={<Search size={15} aria-hidden />}
        />
      </div>

      {error === null ? null : (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}

      <section className="flex flex-col gap-2">
        {loading && ready.length === 0 ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : shown.length === 0 ? (
          /* Qidiruv natija bermagani bilan navbat bo'shligi BOSHQA
             narsa: birinchisida terilgan matnni o'zgartirish kerak,
             ikkinchisida esa qiladigan ish yo'q. */
          <EmptyState
            className="rounded-[var(--radius-lg)] border border-border bg-surface-2"
            icon={<ScanLine size={20} aria-hidden />}
            title={t(ready.length === 0 ? 'handover.readyEmpty' : 'handover.searchEmpty')}
            description={t(
              ready.length === 0 ? 'handover.readyEmptyHint' : 'handover.searchEmptyHint',
            )}
          />
        ) : (
          <ul className="grid gap-3 lg:grid-cols-2">
            {shown.map((row, index) => (
              <li key={row.requisition.id} className="min-w-0">
                <button
                  type="button"
                  onClick={() => onPick(row.requisition.id)}
                  style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
                  className={cn(
                    'motion-slide-in flex h-full w-full flex-col gap-3 text-left',
                    'rounded-[var(--radius-lg)] border border-border border-l-4',
                    'border-l-[var(--color-accent)] bg-surface p-4 shadow-[var(--shadow-1)]',
                    'transition-[border-color,box-shadow] duration-[var(--motion-base)]',
                    'ease-[var(--ease-standard)] hover:shadow-[var(--shadow-2)]',
                  )}
                >
                  {/*
                    TREBOVANIYA RAQAMI — kartochkaning sarlavhasi.

                    Skladchining qo'lida aynan shu raqamli qog'oz turadi va
                    u ro'yxatdan o'shani izlaydi. Talabnoma raqami esa ichki
                    hujjat raqami: u qog'ozda umuman yo'q va sarlavhada
                    turgani skladchini har safar "bu o'shanikimi?" degan
                    savolga qoldirardi.
                  */}
                  <span className="flex items-start gap-3">
                    <span
                      aria-hidden
                      className={cn(
                        'flex size-10 shrink-0 items-center justify-center',
                        'rounded-[var(--radius-md)] bg-accent-soft text-accent',
                      )}
                    >
                      <FileText size={19} />
                    </span>
                    <span className="flex min-w-0 flex-col">
                      <span className="tabular text-base leading-snug font-semibold text-text">
                        {row.formNo ?? (
                          <span className="font-normal text-text-faint">
                            {t('handover.readyNoForm')}
                          </span>
                        )}
                      </span>
                      <span className="tabular text-xs text-text-faint">
                        {row.requisition.docNo}
                      </span>
                    </span>
                    <Badge tone="accent" className="ml-auto shrink-0">
                      {t('handover.readyBadge')}
                    </Badge>
                  </span>

                  {row.requisition.purpose === '' ? null : (
                    <span className="line-clamp-2 text-sm text-pretty text-text-muted">
                      {row.requisition.purpose}
                    </span>
                  )}

                  {/* NIMA BERILADI — skladchi javonga borishdan OLDIN
                      biladi. Ilgari buni bilish uchun talabnomani ochib,
                      keyin orqaga qaytish kerak edi. */}
                  {row.lines.length === 0 ? null : (
                    <span className="flex flex-col gap-1 rounded-[var(--radius-md)] bg-surface-2 px-3 py-2">
                      {row.lines.slice(0, SHOWN_LINES).map((line) => {
                        const nomenclature = nomenclatures.get(line.nomenclatureId);
                        return (
                          <span key={line.id} className="flex items-baseline gap-2 text-xs">
                            <Package
                              size={12}
                              aria-hidden
                              className="shrink-0 self-center text-text-faint"
                            />
                            <span className="min-w-0 truncate text-text">
                              {nomenclature?.name ?? `#${line.nomenclatureId}`}
                            </span>
                            <span className="tabular ml-auto shrink-0 font-medium text-text">
                              {qtyText(line.qty)}{' '}
                              <span className="font-normal text-text-faint">
                                {nomenclature === undefined ? '' : unitText(nomenclature.unit)}
                              </span>
                            </span>
                          </span>
                        );
                      })}
                      {row.lines.length > SHOWN_LINES ? (
                        <span className="text-xs text-text-faint">
                          {t('handover.readyMoreLines', {
                            count: row.lines.length - SHOWN_LINES,
                          })}
                        </span>
                      ) : null}
                    </span>
                  )}

                  <span className="mt-auto flex items-center gap-1.5 text-sm font-medium text-accent">
                    {t('handover.readyIssue')}
                    <ArrowRight size={14} aria-hidden />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};
